import { useMemo } from "react";
import type { UnitCategory } from "verdi-common/Configurations/SensorConfigurations/SensorConfigurationTypes";
import { useListDataStreams } from "@/api/orval/generated/data-stream";
import type { ListDataStreams200Item, ListDataStreamsParams } from "@/api/orval/generated/schemas";
import { type DashboardSpec, type PanelSpec, SPEC_VERSION, type StreamEntry } from "../ChartSpec";
import { DEFAULT_RELATIVE_DURATION } from "../time-window/time-range-slice";
import {
    buildAllStreamsPanel,
    buildCategoryPanels,
    buildRecipePanels,
    irrigationBand,
    type PanelRecipe,
    toStreamEntries,
} from "./panel-builders";
import { isPlottableStream, type ResolvedStream, resolveStreams, toResolvedStream } from "./resolve-streams";
import { selectStreams } from "./select-streams";
import type { UnitCategoryPresentation } from "./unit-category-presentation";
import { useOrgDataStreams } from "./use-org-data-streams";

const DEFAULT_TIME_RANGE = { mode: "relative", duration: DEFAULT_RELATIVE_DURATION } as const;

const NO_ADDED_IDS: readonly number[] = Object.freeze([]);

/** "single" is every stream on one panel; "stacked" is one panel per unit category. */
export type PanelLayout = "single" | "stacked";

/**
 * What the card putting the viewer on screen asks the generator for. Every field is an opinion the
 * card holds about its own metrics, so leaving one out is not a lesser viewer — it is a card with
 * nothing to say about that, and the generator falls back to the order the API sent and hashed
 * colors. Nothing here is applied to every card.
 */
export type DataViewerSpecOptions = {
    /**
     * Unit categories to leave out — a weather station card has no use for the station's voltage.
     * Whole categories only: a card wanting one stream of a category gone needs something finer.
     */
    excludedUnitCategories?: UnitCategory[];
    /** Where each category's panel sits in the stack, and what color its series take. */
    unitCategoryPresentation?: UnitCategoryPresentation[];
    /** Categories the card wants drawn on one panel — rain against temperature, gust against speed. */
    panelRecipes?: PanelRecipe[];
    /** Scheduled and actual irrigation over the first panel, where the legacy strip sat. Zones only. */
    irrigationBand?: boolean;
};

export type DataViewerSpecResult = {
    spec: DashboardSpec;
    isLoading: boolean;
    isError: boolean;
    isEmpty: boolean;
};

function toSpec(panels: PanelSpec[]): DashboardSpec {
    return { version: SPEC_VERSION, defaultTimeRange: DEFAULT_TIME_RANGE, panels };
}

function zonePanels(entries: StreamEntry[], layout: PanelLayout): PanelSpec[] {
    if (layout === "stacked") return buildCategoryPanels(entries);

    const panel = buildAllStreamsPanel(entries);
    return panel ? [panel] : [];
}

// No paired panels here whatever the card asks for: a zone holds many devices, so pairing would put
// one station's rain against another's temperature.
export function getZoneSpec(
    zoneId: string,
    resolvedStreams: ResolvedStream[],
    layout: PanelLayout,
    options: DataViewerSpecOptions = {},
): DashboardSpec {
    const panels = zonePanels(toStreamEntries(resolvedStreams, options.unitCategoryPresentation ?? []), layout);
    if (!options.irrigationBand || panels.length === 0) return toSpec(panels);

    const [first, ...rest] = panels;
    return toSpec([{ ...first, bands: [irrigationBand(zoneId, { position: "above" })] }, ...rest]);
}

export function getDeviceSpec(
    resolvedStreams: ResolvedStream[],
    { unitCategoryPresentation = [], panelRecipes = [] }: DataViewerSpecOptions = {},
): DashboardSpec {
    return toSpec(buildRecipePanels(toStreamEntries(resolvedStreams, unitCategoryPresentation), panelRecipes));
}

/**
 * The same options back, holding one reference for as long as the contents match. A card passing a
 * fresh literal every render would otherwise rebuild the spec each time, and a new spec replaces
 * the document — losing the time range and axis the viewer is sitting on.
 */
function useStableOptions(options: DataViewerSpecOptions): DataViewerSpecOptions {
    const key = JSON.stringify(options);
    return useMemo(() => JSON.parse(key) as DataViewerSpecOptions, [key]);
}

function useResolvedStreams(
    params: ListDataStreamsParams,
    enabled: boolean,
    // No `= []` default: a fresh array every render rebuilds the spec, and a new spec replaces the document.
    excludedUnitCategories: UnitCategory[] | undefined,
) {
    const { data, isLoading, isError } = useListDataStreams(params, { query: { enabled } });

    const resolvedStreams: ResolvedStream[] = useMemo(
        () => (data ? resolveStreams(data, excludedUnitCategories) : []),
        [data, excludedUnitCategories],
    );

    return {
        resolvedStreams,
        isLoading,
        isError,
        isEmpty: !isLoading && !isError && resolvedStreams.length === 0,
    };
}

/** Think of this as the configuration file for how a zone-based DataViewer should be rendered. */
export function useZoneDataViewerSpec(
    zoneId: string,
    layout: PanelLayout,
    options: DataViewerSpecOptions = {},
    addedIds: readonly number[] = NO_ADDED_IDS,
): DataViewerSpecResult & {
    plottedStreams: ListDataStreams200Item[];
    isPlottable: (stream: ListDataStreams200Item) => boolean;
} {
    const stableOptions = useStableOptions(options);
    const { excludedUnitCategories } = stableOptions;
    const { data: zoneData, isLoading, isError } = useListDataStreams({ zoneId }, { query: { enabled: !!zoneId } });
    const org = useOrgDataStreams(addedIds.length > 0);

    // Disabled, but still gets the picker's cached data; reading it would rebuild the spec for nothing.
    const orgData = addedIds.length > 0 ? org.data : undefined;

    const isPlottable = useMemo(() => {
        const excluded = new Set(excludedUnitCategories ?? []);
        return (stream: ListDataStreams200Item) => isPlottableStream(stream, excluded);
    }, [excludedUnitCategories]);
    const selected = useMemo(
        () => selectStreams(zoneData ?? [], orgData ?? [], addedIds).filter(isPlottable),
        [zoneData, orgData, addedIds, isPlottable],
    );
    const resolvedStreams = useMemo(() => selected.map(toResolvedStream), [selected]);

    // SpecProvider replaces the document when initialSpec's reference changes — keep it stable.
    const spec = useMemo(
        () => getZoneSpec(zoneId, resolvedStreams, layout, stableOptions),
        [zoneId, resolvedStreams, layout, stableOptions],
    );

    return {
        spec,
        isLoading,
        isError,
        isEmpty: !isLoading && !isError && resolvedStreams.length === 0,
        plottedStreams: selected,
        isPlottable,
    };
}

/**
 * Think of this as the configuration file for how a device-based DataViewer should be rendered.
 *
 * @param host - The host device that owns the streams. The streams API rejects a device id without its
 *     collection, so pass both or nothing.
 */
export function useDeviceDataViewerSpec(
    host: { id: string; collection: string } | undefined,
    options: DataViewerSpecOptions = {},
): DataViewerSpecResult {
    const stableOptions = useStableOptions(options);
    const { resolvedStreams, ...status } = useResolvedStreams(
        { deviceId: host?.id, deviceCollection: host?.collection },
        !!host,
        stableOptions.excludedUnitCategories,
    );

    // SpecProvider replaces the document when initialSpec's reference changes — keep it stable.
    const spec = useMemo(() => getDeviceSpec(resolvedStreams, stableOptions), [resolvedStreams, stableOptions]);

    return { spec, ...status };
}
